import { useCallback } from 'react'
import {
  ComplianceFramework,
  ComplianceFrameworks,
  Soc2TrustServiceCriteriaCategories,
} from '@trustero/trustero-api-web/lib/audit/framework_pb'
import { AuditPromiseClient } from '@trustero/trustero-api-web/lib/audit/audit_grpc_web_pb'
import { ModelPromiseClient } from '@trustero/trustero-api-web/lib/model/model_grpc_web_pb'
import { Empty } from 'google-protobuf/google/protobuf/empty_pb'
import {
  GetComplianceFrameworkRequest,
  GetComplianceFrameworksRequest,
  GetHasComplianceFrameworksRequest,
  GetHasComplianceFrameworksResponse,
  GetSoc2TrustServiceCriteriaRequest,
  GetTemplateFrameworkAvailabilityResponse,
} from '@trustero/trustero-api-web/lib/audit/audit_pb'
import { AccountPromiseClient } from '@trustero/trustero-api-web/lib/account/account_grpc_web_pb'
import {
  EnableComplianceFrameworksRequest,
  TemplateFilter,
  TemplateSelected,
} from '@trustero/trustero-api-web/lib/account/account_pb'
import { FRAMEWORK_MODEL_IDS } from 'src/Utils/globalEnums'
import { GrpcResponse, NewGrpcResponse } from '../hooks/types'
import { useSwrImmutableGrpc } from '../useSwrImmutableGrpc'
import { useGrpcRevalidateByMethod } from '../useGrpcMutate'
import { useAuthorizedGrpcClient } from '../../../adapter/grpcClient'
import { useInvalidatePoliciesCache } from '../policy/usePolicies'
import { useInvalidateControlsCache } from '../model/control/useControls'
import { useFrameworkWizard } from '../../../context/FormContext'

// Invalidates the cache for getComplianceFrameworks requests and getHasComplianceFrameworks requests
export const useInvalidateComplianceFrameworksCache =
  (): (() => Promise<void>) => {
    const mutateFunc = useGrpcRevalidateByMethod()

    return useCallback(async () => {
      await Promise.all([
        mutateFunc(AuditPromiseClient.prototype.getComplianceFrameworks),
        mutateFunc(AuditPromiseClient.prototype.getHasComplianceFrameworks),
      ])
    }, [mutateFunc])
  }

// Get all compliance frameworks with the option to filter by if they are enabled on the current account.
// Pass a pre-built request to use the new audit/control/type filter fields; otherwise the
// excludeDisabled boolean is honored for backwards compatibility with existing call sites.
export const useComplianceFrameworks = (
  excludeDisabled?: boolean,
  request?: GetComplianceFrameworksRequest,
): GrpcResponse<ComplianceFrameworks> => {
  let resolvedRequest = request
  if (!resolvedRequest) {
    resolvedRequest = new GetComplianceFrameworksRequest()
    if (excludeDisabled) {
      resolvedRequest.setExcludeDisabled(true)
    }
  }
  const { response } = useSwrImmutableGrpc(
    AuditPromiseClient.prototype.getComplianceFrameworks,
    resolvedRequest,
  )
  return NewGrpcResponse(response)
}

// Fetch a single compliance framework by its ID
export const useComplianceFramework = (
  complianceFrameworkId: string,
  shouldFetch = true,
): GrpcResponse<ComplianceFramework> => {
  const request = new GetComplianceFrameworkRequest().setComplianceFrameworkId(
    complianceFrameworkId,
  )
  const { response } = useSwrImmutableGrpc(
    AuditPromiseClient.prototype.getComplianceFramework,
    request,
    shouldFetch,
  )
  return NewGrpcResponse(response)
}

// Fetch the SOC 2 Trust Service Criteria categories included in the account's scope for a
// framework. Only meaningful for the SOC 2 framework, so callers should gate shouldFetch on that.
export const useSoc2TrustServiceCriteria = (
  complianceFrameworkId: string,
  shouldFetch = true,
): GrpcResponse<Soc2TrustServiceCriteriaCategories> => {
  const request =
    new GetSoc2TrustServiceCriteriaRequest().setComplianceFrameworkId(
      complianceFrameworkId,
    )
  const { response } = useSwrImmutableGrpc(
    AuditPromiseClient.prototype.getSoc2TrustServiceCriteria,
    request,
    shouldFetch,
  )
  return NewGrpcResponse(response)
}

// Returns compliance frameworks available for this account to enable from the linked
// content account's latest published content version.
// Callers mounted outside an open modal should pass shouldFetch to defer the RPC.
export const useTemplateFrameworks = (
  shouldFetch = true,
): GrpcResponse<ComplianceFrameworks> => {
  const { response } = useSwrImmutableGrpc(
    AuditPromiseClient.prototype.getTemplateFrameworks,
    new Empty(),
    shouldFetch,
  )
  return NewGrpcResponse(response)
}

// Whether this account has a catalog to offer at all — it is linked to a content
// account that has published a content version — and whether that is still unknown.
// Unlike useTemplateFrameworks this does not read the snapshot, so it is cheap enough
// to gate an affordance on every render.
//
// isLoading is load-bearing. This RPC is called only from the add-framework entry
// points, so nothing else warms its cache and `hasCatalog: false` means "not yet
// known" for a real interval on first visit. Anything deciding where a click goes
// must not read that as "no catalog". It is false when shouldFetch is false, since
// nothing is pending, and false on error, where we fail closed to the custom form.
export const useTemplateFrameworkAvailability = (
  shouldFetch = true,
): { hasCatalog: boolean; isLoading: boolean } => {
  const { response } = useSwrImmutableGrpc(
    AuditPromiseClient.prototype.getTemplateFrameworkAvailability,
    new Empty(),
    shouldFetch,
  )
  const { data, loading } =
    NewGrpcResponse<GetTemplateFrameworkAvailabilityResponse>(response)
  return {
    hasCatalog: Boolean(data?.getAvailable()),
    isLoading: shouldFetch && loading,
  }
}

// Returns boolean telling if current account has enabled any compliance frameworks
export const useHasComplianceFramework = (
  modelId?: string,
): GrpcResponse<GetHasComplianceFrameworksResponse> => {
  const request = new GetHasComplianceFrameworksRequest()

  if (modelId) {
    request.setModelId(modelId)
  }

  const { response } = useSwrImmutableGrpc(
    AuditPromiseClient.prototype.getHasComplianceFrameworks,
    request,
  )
  return NewGrpcResponse(response)
}

export const useHasSoc2 = (): boolean => {
  const { data } = useHasComplianceFramework(FRAMEWORK_MODEL_IDS.SOC2)

  return data?.getHasenabled() ?? false
}

// Enable a compliance framework - pulls data from the useFrameworkWizard context
export const useEnableComplianceFramework = (): (() => Promise<void>) => {
  const client = useAuthorizedGrpcClient(AccountPromiseClient)
  const frameworkMutator = useInvalidateComplianceFrameworksCache()
  const controlMutator = useInvalidateControlsCache()
  const policyMutator = useInvalidatePoliciesCache()
  const revalidateByMethod = useGrpcRevalidateByMethod()

  const {
    controlsState: { suggestedControls, selectedControls },
    policiesState: { suggestedPolicies, selectedPolicies },
    frameworkState: { framework },
  } = useFrameworkWizard()

  return useCallback(async () => {
    const finalSelectedControls = new Set(selectedControls)
    const finalSelectedPolicies = new Set(selectedPolicies)
    framework &&
      (await client.enableComplianceFrameworks(
        new EnableComplianceFrameworksRequest()
          .addComplianceFrameworkIds(framework.getModelId())
          .setTemplateFilter(
            new TemplateFilter()
              .setControlFilterList(
                suggestedControls.map((control) => {
                  return new TemplateSelected()
                    .setModelId(control.getModelId())
                    .setSelected(
                      finalSelectedControls.has(control.getModelId()),
                    )
                }),
              )
              .setPolicyFilterList(
                suggestedPolicies.map((policy) => {
                  return new TemplateSelected()
                    .setModelId(policy.getModelId())
                    .setSelected(finalSelectedPolicies.has(policy.getModelId()))
                }),
              ),
          ),
      ))
    await Promise.all([
      frameworkMutator(),
      controlMutator(),
      policyMutator(),
      revalidateByMethod(ModelPromiseClient.prototype.listFrameworks),
      revalidateByMethod(AuditPromiseClient.prototype.getAudits),
    ])
  }, [
    client,
    controlMutator,
    framework,
    frameworkMutator,
    policyMutator,
    revalidateByMethod,
    selectedControls,
    selectedPolicies,
    suggestedControls,
    suggestedPolicies,
  ])
}
